import { useEffect, useRef, useState } from "react";
import API from "../../models/API";
import { ExtractedTable } from "../../models/ExtractedTable";
import { Matcher } from "../../models/matchers/Matcher";
import { TableOffsetMessages } from "../../models/messages/Messages";
import Page, { PageSpans } from "../../models/Page";
import eventEmitter from "../../state/EventEmitter";
import "../../styles/tableEditing/tableOffset.css";
import {
  DialogBoxConfirmEvent,
  DialogBoxYesNoEvent,
} from "../../types/DialogBoxEvents";
import { Sleep } from "../../utils/Sleep";
import { OnOffToggle } from "../buttons/OnOffToggle";
import { ToggleButton } from "../buttons/ToggleButton";
import { useAsync } from "../hooks/useAsync";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTUseOffset } from "../tooltips/TTTables";

export { TableOffset };

type Props = {
  startPattern: Matcher;
  handleOffset: boolean;
  startOffset: number;
  startOffsetEnd: number;
  update: (
    handleOffset: boolean,
    startOffset: number,
    startOffsetEnd: number,
  ) => void;
  /** AcceleratorDoc Uuid for Table Offset guessing */
  acceleratorDocUuid: string;
  /** Extracted Table Information. Drives visibility of Table Offset guessing. */
  extractedData?: ExtractedTable | undefined;
  /** Spans Promise */
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
  /** List of Pages on the AcceleratorDoc */
  pages: Page[];
  // offsetGuessButton?: ReactNode;
};

const TableOffset = (props: Props) => {
  const spanPromise = useRef(props.getPageSpansById).current;
  const [handleOffset, setHandleOffset] = useState<boolean>(
    props.handleOffset ?? false,
  );

  /** If guess results is null, that means no start pattern was found. Undefined means not yet guessed or error, ignore. */
  const [guessResults, triggerGuess, guessing, setGuess] = useAsync(
    async () => {
      if (!props.extractedData) return null;
      // TODO: delay offset guess until extraction complete
      const extractedData = props.extractedData!;
      if (!extractedData!.rows.find((x) => x.rowType === "START")) {
        // sleep for 1.5 seconds, try again
        await Sleep(1500);
        if (!extractedData!.rows.find((x) => x.rowType === "START")) {
          return null;
        }
      }

      const startRow = extractedData!.rows.find((x) => x.rowType === "START")!;
      const data = await API.post(
        `/v0/matcher/${props.acceleratorDocUuid}/matchRow`,
        {
          pageRowNumber: startRow.pageRowNumber,
          pageId: startRow.pageId,
        },
        props.startPattern.toMatcherData(),
      ).catch((e) => {
        console.log(e);
        return null;
      });
      const spanUuids = ((await data?.json()) as string[]) ?? [];
      let offsetSpans;
      if (spanUuids.length > 0) {
        // set offset from Spans
        const allSpans = (await spanPromise(startRow.pageId)).spans;
        offsetSpans = allSpans
          .filter((x) => spanUuids.includes(x.id))
          .sort((a, b) => a.x0 - b.x0);
      }
      if (offsetSpans && offsetSpans.length) {
        const page = props.pages[offsetSpans[0].pageIndex];
        if (!page || page.width === 0) return null;

        const startX = offsetSpans[0].x0;
        const endX = offsetSpans[offsetSpans.length - 1].x1;
        const absStartOffset = (startX / page!.width) * 100;
        const absStartOffsetEnd = (endX / page!.width) * 100;
        return [absStartOffset, absStartOffsetEnd];
      }
      return;
    },
    undefined,
  );

  // trigger update when guess completee
  useEffect(() => {
    if (guessResults) {
      props.update(handleOffset, guessResults[0], guessResults[1]);
    } else if (guessResults == null && typeof guessResults != "undefined") {
      eventEmitter.publish(
        new DialogBoxConfirmEvent(
          TableOffsetMessages.notCalculated(),
          (confirmed: boolean) => {
            setHandleOffset(false);
            setGuess(undefined);
          },
        ),
      );
      props.update(false, 0, 0);
    }
  }, [guessResults]);

  // update state when prop changes
  useEffect(() => {
    if (props.handleOffset !== handleOffset) {
      setHandleOffset(props.handleOffset);
    }
  }, [props.handleOffset]);

  // trigger offset guess when handleOffset enabled
  useEffect(() => {
    if (props.handleOffset !== handleOffset) {
      if (handleOffset) {
        triggerGuess();
      } else {
        props.update(false, 0, 0);
      }
    }
  }, [handleOffset]);

  if (guessing) {
    return (
      <ToggleButton
        active={true}
        onClick={() => null}
        className={"tableOffset padded"}
      >
        Calculating...
      </ToggleButton>
    );
  }

  return (
    <AdvancedTooltip tooltipText={TTUseOffset} overflow>
      {(tProps) => (
        <OnOffToggle
          tProps={tProps}
          className={"tableOffset padded"}
          active={handleOffset}
          onClick={() => {
            if (props.extractedData) {
              setHandleOffset(!handleOffset);
            } else if (handleOffset) {
              // let user disable offsets for table templates in crud
              // TODO: different error message ?
              eventEmitter.publish(
                new DialogBoxYesNoEvent(
                  TableOffsetMessages.crud_cantCalculateDisable(),
                  (confirmed: boolean) => {
                    if (confirmed) {
                      setHandleOffset(false);
                    }
                  },
                  undefined,
                  "Warning",
                ),
              );
            } else {
              eventEmitter.publish(
                new DialogBoxConfirmEvent(
                  TableOffsetMessages.crud_cantCalculate(),
                  (confirmed: boolean) => {
                    if (confirmed) {
                      setHandleOffset(false);
                    }
                  },
                ),
              );
            }
          }}
        >
          Use Offset
        </OnOffToggle>
      )}
    </AdvancedTooltip>
  );
};
